import { useState, useEffect, useCallback, useRef } from "react";
import {
  getSources,
  getMappings,
  getEmissionKpiSettings,
  getFrameworks,
  getReportingQuestions,
} from "./kpiAssetApi";
import { safeParseJSON } from "./helpers";

/**
 * Owns everything the page fetches for one financial year.
 *
 * - `loading` is true only for blocking loads (first load / FY switch) so the
 *   page can show a spinner without unmounting anything on later refreshes.
 * - `refresh()` re-fetches in the background after mutations; rows keep their
 *   expanded/edit state and simply re-render with fresh data.
 * - Responses are tagged with a fetch id; anything that resolves after a newer
 *   fetch started is dropped, so a slow response can't clobber a newer FY.
 */
export function useKpiAssetsData(fyId) {
  const [allQuestions, setAllQuestions] = useState([]);
  const [kpiAssetMap, setKpiAssetMap] = useState({});
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!fyId) {
      setLoading(false);
      return;
    }
    const fetchId = ++fetchIdRef.current;
    const isCurrent = () => fetchId === fetchIdRef.current;

    if (background) setRefreshing(true);
    else setLoading(true);

    try {
      const [locRes, mappingsRes, emissionSettingsRes] = await Promise.all([
        getSources(),
        getMappings(fyId),
        getEmissionKpiSettings(fyId),
      ]);

      /**
       * Allowed KPI ID set derived from emissionKpiSettings; null means no
       * saved settings yet → fall back to the kpi_type EMISSION filter below.
       */
      const emissionRaw = emissionSettingsRes?.data;
      const allowedKpiIds = Array.isArray(emissionRaw?.kpiIds)
        ? new Set(emissionRaw.kpiIds.map(Number))
        : null;

      const userId = safeParseJSON(localStorage.getItem("user_temp_id"));
      const fwRes = await getFrameworks(userId);
      const frameworks = fwRes?.data?.data || [];
      const qRes = await getReportingQuestions(fyId, frameworks.map(f => f.id));
      const allFetched = qRes?.data?.data || [];

      const questions = allowedKpiIds
        ? allFetched.filter(q => allowedKpiIds.has(Number(q.questionId)))
        : allFetched.filter(q => q.kpi_type?.includes("EMISSION"));

      /**
       * Server mappings keyed by KPI id. Keys are pre-seeded from the
       * filtered question list only: every consumer looks the map up via
       * allQuestions, so mappings for KPIs outside it (orphans left behind
       * by emission-settings changes) are intentionally dropped rather than
       * carried as unreadable entries.
       */
      const serverMappings = mappingsRes?.data?.data || [];
      const map = {};
      for (const q of questions) map[q.questionId] = [];
      for (const m of serverMappings) {
        map[m.kpiId ?? m.kpi_id]?.push(m);
      }

      if (!isCurrent()) return;

      setSources(locRes?.data?.data || []);
      setAllQuestions(questions);
      setKpiAssetMap(map);
      setError(null);
    } catch (e) {
      console.error(e);
      if (isCurrent()) {
        setError("Failed to load KPI assets. Please try again.");
      }
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [fyId]);

  useEffect(() => {
    load();
  }, [load]);

  /** Background refresh for after-mutation updates — never blanks the page. */
  const refresh = useCallback(() => load({ background: true }), [load]);

  /** Blocking reload for the error-state Retry button. */
  const reload = useCallback(() => load(), [load]);

  return { allQuestions, kpiAssetMap, sources, loading, refreshing, error, refresh, reload };
}
